import { useEffect } from 'react';
import { TextField, Typography } from '@gupshup-frontend/gupshup-ts-components';
import {
  BUTTON_TYPE,
  createButton,
  isAuthentication,
  isAutofill,
  isCopyCode,
  isPasskey,
  isZeroTap,
} from '../state';
import { friendlyError } from './errorMessages';

// DTO-exact (Form/validate.js's own BUTTON_TEXT_MAX_CHARS) — duplicated locally since validate.js
// doesn't export it, same convention as Header.js/Footer.js's own local HEADER_TEXT_MAX_CHARS/
// FOOTER_MAX_CHARS.
const BUTTON_TEXT_MAX_CHARS = 25;

// The one auth button this card's fields read/write through. state.buttons holds at most one OTP
// or PASSKEY entry once the seed effect below has run — see otpButtons/passkeyButtons in
// payload.js's bucketButtons. Duplicated in AuthAppSetup.js (its zero-tap checkbox needs the same
// lookup) rather than shared — same small-local-duplication convention as BUTTON_TEXT_MAX_CHARS
// above. Returns undefined only in the brief window before the seed effect's first dispatch lands.
function otpOrPasskeyButton(state) {
  return state.buttons.find(
    (btn) => btn.type === BUTTON_TYPE.OTP || btn.type === BUTTON_TYPE.PASSKEY,
  );
}

// Auth buttons text — Authentication-only card for the auth button's own copy-code / autofill
// text fields.
//
// This is also the ONLY place that ever puts an OTP/PASSKEY button into state.buttons — see the
// effect immediately below.
const AuthButtonsTextSection = ({ state, dispatch, errors, disabled }) => {
  // --- Structural gap this effect closes ----------------------------------------------------
  // payload.js's bucketButtons() only emits otpButtons/passkeyButtons when state.buttons already
  // contains a button of type OTP/PASSKEY. Nothing else in this form ever creates one —
  // Category.js's handleCategoryChange/handleSubCategoryChange only ever RESET buttons to [].
  // Left alone, every Authentication template submits with zero auth buttons, and validate.js's
  // own exactly_one_otp_button_required check (buttons.OTP, for copy_code/autofill/zero_tap) can
  // never pass. This effect is what actually creates/replaces that button, keyed off subcategory:
  //   - Passkey                          -> exactly one PASSKEY button.
  //   - copy_code / autofill / zero_tap  -> exactly one OTP button.
  //
  // Lives here (rather than AuthMessageContent/AuthAppSetup) because this is the card whose own
  // fields (copy-code/autofill text below) read and write the seeded button. It's declared ahead
  // of this component's own `!passkey` JSX gate further down, so it still fires — and correctly
  // seeds the PASSKEY button — even on a render where this card itself shows no visible content
  // (every section in sections/index.js's SECTIONS array is always mounted and re-invoked on
  // every render regardless of what JSX it returns, same pattern Header.js's own file-header
  // comment documents; only the return value, never the hook, is conditional).
  //
  // MUST stay idempotent. state.buttons already holding exactly one button of the right type is
  // a no-op — the guard below is what makes that true. Without it, this effect would re-fire on
  // every render this component takes part in, including the ones caused by the user typing into
  // the very text/autofillText fields this same button owns a bit further down, and would
  // dispatch removeButton+addButton on every keystroke — wiping whatever was just typed. Do not
  // "simplify" this into an unconditional createButton() call.
  useEffect(() => {
    if (!isAuthentication(state)) return;

    let targetType;
    if (isPasskey(state)) {
      targetType = BUTTON_TYPE.PASSKEY;
    } else if (isCopyCode(state) || isAutofill(state) || isZeroTap(state)) {
      targetType = BUTTON_TYPE.OTP;
    } else {
      // Category is Authentication but no subcategory has landed yet — nothing to reconcile
      // until one has (Category.js sets both in the same render pass, so this is momentary).
      return;
    }

    const authButtons = state.buttons.filter(
      (btn) => btn.type === BUTTON_TYPE.OTP || btn.type === BUTTON_TYPE.PASSKEY,
    );
    if (authButtons.length === 1 && authButtons[0].type === targetType) {
      // Already correct — bail without dispatching so user-entered text/autofillText/
      // zeroTapTermsAccepted on the existing button survives this render untouched.
      return;
    }

    authButtons.forEach((btn) => dispatch({ type: 'removeButton', buttonId: btn.buttonId }));
    dispatch({ type: 'addButton', button: createButton(targetType) });
    // Deliberately NOT depending on state.buttons — see the idempotency comment above. Re-run
    // only when the subcategory decision that picks targetType could have changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.category.value, state.category.selectedSubcategory.value]);

  if (!isAuthentication(state) || isPasskey(state)) return null;

  const needsAutofillText = isAutofill(state) || isZeroTap(state);
  const authButton = otpOrPasskeyButton(state);
  const authButtonIndex = authButton
    ? state.buttons.findIndex((btn) => btn.buttonId === authButton.buttonId)
    : -1;

  return (
    <div className="br-tpl-form-card" data-testid="bloomreach-templates-form-auth-button-text">
      <div className="br-tpl-form-card-heading-row">
        <div className="br-tpl-form-card-heading">
          <Typography variant="subtitle1" data-testid="bloomreach-templates-form-auth-button-text-heading">
            Buttons text
          </Typography>
        </div>
      </div>
      <Typography variant="caption" className="br-tpl-form-helper-text">
        You can customize the button text for both autofill and copy code. Even when zero-tap is selected, buttons are still needed for the backup code delivery method.
      </Typography>

      {/* The console hardcodes a `disabled` prop on both fields below, which directly
          contradicts its own helper text above promising the button text is customizable.
          We deliberately don't copy that — both fields stay editable via the existing
          disabled={disabled || !authButton} only. */}
      <TextField
        fullWidth
        label="Copy code button text"
        value={authButton?.text.value ?? ''}
        onChange={(event) => authButton && dispatch({
          type: 'updateButton',
          buttonId: authButton.buttonId,
          value: { text: { value: event.target.value, error: '' } },
        })}
        disabled={disabled || !authButton}
        placeholder="copy code"
        error={Boolean(errors[`buttons.${authButtonIndex}.text.value`])}
        helperText={friendlyError(errors[`buttons.${authButtonIndex}.text.value`])}
        autoComplete="off"
        inputProps={{
          maxLength: BUTTON_TEXT_MAX_CHARS,
          'data-testid': 'bloomreach-templates-form-auth-button-text-field',
        }}
      />

      {needsAutofillText && (
        <TextField
          fullWidth
          label="Autofill button text"
          value={authButton?.autofillText.value ?? ''}
          onChange={(event) => authButton && dispatch({
            type: 'updateButton',
            buttonId: authButton.buttonId,
            value: { autofillText: { value: event.target.value, error: '' } },
          })}
          disabled={disabled || !authButton}
          placeholder="Autofill"
          error={Boolean(errors[`buttons.${authButtonIndex}.autofillText.value`])}
          helperText={friendlyError(errors[`buttons.${authButtonIndex}.autofillText.value`])}
          autoComplete="off"
          inputProps={{
            maxLength: BUTTON_TEXT_MAX_CHARS,
            'data-testid': 'bloomreach-templates-form-auth-autofill-text-field',
          }}
        />
      )}
    </div>
  );
};

export default AuthButtonsTextSection;
